import { AppBar, Avatar, Box, Container, IconButton, Menu, MenuItem, Toolbar, Tooltip, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import { AccountCircle, Logout } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import axios from "../api/axios";
import { getImageUrl } from '../utils/imageUtils';
import useAxiosPrivate from '../hooks/useAxiosPrivate';

const Header = ({ drawerWidth, onMenuClick }) => {
    const [userMenu, setUserMenu] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();
    const { auth, setAuth } = useAuth();
    const [customerDb, setCustomerDb] = useState(auth?.customerDatabase || null);
    const [userAvatar, setUserAvatar] = useState(null);
    const axiosPrivate = useAxiosPrivate();

    useEffect(() => {
        async function fetchDb() {
            if (!auth?.customerDatabase && auth?.email) {
                try {
                    const res = await axios.get(`/auth/customer-db?email=${encodeURIComponent(auth.email)}`);
                    setCustomerDb(res.data.database);
                } catch (err) {
                    setCustomerDb(null);
                }
            } else if (auth?.customerDatabase) {
                setCustomerDb(auth.customerDatabase);
            }
        }
        fetchDb();
    }, [auth]);

    useEffect(() => {
        async function fetchUserAvatar() {
            if (auth?.email) {
                try {
                    const res = await axiosPrivate.get('/auth/profile');
                    if (res.data.avatar) {
                        // Aggiungi timestamp per cache busting
                        const timestamp = new Date().getTime();
                        setUserAvatar(`${getImageUrl(res.data.avatar)}?t=${timestamp}`);
                    }
                } catch (err) {
                    console.error('Errore caricamento avatar:', err);
                    setUserAvatar(null);
                }
            }
        }
        fetchUserAvatar();
    }, [auth?.email, axiosPrivate]);

    const handleOpenUserMenu = (event) => {
        setAnchorEl(event.currentTarget);
        setUserMenu(true);
    };

    const handleCloseUserMenu = () => {
        setAnchorEl(null);
        setUserMenu(false);
    };

    const handleMenuClick = (setting) => {
        handleCloseUserMenu();
        
        switch(setting) {
            case 'Account':
                navigate(`${process.env.REACT_APP_BASE_PATH || '/dashboard'}/account`);
                break;
            case 'Logout':
                handleLogout();
                break;
            default:
                break;
        }
    };

    const handleLogout = async () => {
        try {
            await axios.post('/auth/logout');
            setAuth({});
            navigate('/login');
        } catch (error) {
            // Logout locale anche se il server non risponde
            setAuth({});
            navigate('/login');
        }
    };

    return (
        <AppBar
            position="fixed"
            sx={{
                width: { sm: `calc(100% - ${drawerWidth}px)` },
                ml: { sm: `${drawerWidth}px` },
            }}
        >
            <Container maxWidth="100%">
                <Toolbar sx={{ justifyContent: "space-between" }} disableGutters>
                    {/* Icona del menu per dispositivi mobili */}
                    <Box sx={{ display: { sm: "none" } }}>
                        <IconButton
                            size="large"
                            aria-label="menu"
                            onClick={onMenuClick}
                            color="inherit"
                        >
                            <MenuIcon />
                        </IconButton>
                    </Box>

                    {/* Menu utente */}
                    <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1, justifyContent: "flex-end" }}>
                        <Tooltip title="Account">
                            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                <Avatar 
                                    alt="User Avatar" 
                                    src={userAvatar}
                                    sx={{ bgcolor: 'secondary.main' }}
                                >
                                    {!userAvatar && auth?.email ? auth.email.charAt(0).toUpperCase() : 'U'}
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                        <Menu
                            sx={{ mt: "45px" }}
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={userMenu}
                            onClose={handleCloseUserMenu}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1 }}>
                                <Avatar 
                                    alt="User Avatar" 
                                    src={userAvatar}
                                    sx={{ bgcolor: 'secondary.main', width: 56, height: 56 }}
                                >
                                    {!userAvatar && auth?.email ? auth.email.charAt(0).toUpperCase() : 'U'}
                                </Avatar>
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontWeight: 500 }}>
                                    {auth?.email || 'Utente non loggato'}
                                </Typography>
                            </Box>
                            <MenuItem onClick={() => handleMenuClick('Account')}>
                                <AccountCircle sx={{ mr: 1 }} />
                                <Typography textAlign="center">Account</Typography>
                            </MenuItem>
                            <MenuItem onClick={() => handleMenuClick('Logout')}>
                                <Logout sx={{ mr: 1 }} />
                                <Typography textAlign="center">Logout</Typography>
                            </MenuItem>
                        </Menu>

                        {/* Email sempre visibile nella barra header */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 2 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                {auth?.email || 'Utente non loggato'}
                            </Typography>
                        </Box>
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    );
};

export default Header;